/**
 * Portfolio Client
 *
 * Fetches wallet holdings and transaction history. Holdings call the DAS
 * API through `solana-rpc-proxy` (Triton primary, Alchemy fallback post
 * Pack 1). Transaction history calls the `helius-transactions` edge fn
 * for enriched parsing; that edge fn is Pack 3 territory and will be
 * refactored to read from `dextoro-market-data` in a follow-up pack.
 */

import { getSolanaEndpoint } from '@/lib/solana/config';
import { supabase } from '@/integrations/supabase/client';
import { debug } from "@/lib/debug";
import { MeteoraDBCClient } from '@/lib/meteora/dbcClient';
import type { PoolInfo } from '@/lib/meteora/types';
import { fetchPoolSnapshot } from '@/lib/tradingview/api';

// Types for holdings
export interface TokenHolding {
  mint: string;
  symbol: string;
  name: string;
  imageUrl: string | null;
  balance: string; // Keep as string to avoid BigInt overflow
  decimals: number;
  uiAmount: string;
  priceUsd: number | null;
  valueUsd: number | null;
  /** Actual on-chain token account pubkey (may differ from ATA for non-ATA accounts). Only set for empty accounts. */
  tokenAccountAddress?: string;
}

export interface PortfolioHoldings {
  solBalance: number;
  solValueUsd: number | null;
  tokens: TokenHolding[];
  /** Token accounts with zero balance — closable to reclaim rent */
  emptyAccounts: TokenHolding[];
  totalValueUsd: number | null;
}

// Types for transactions
export interface TransactionEvent {
  signature: string;
  timestamp: number;
  type: 'SWAP' | 'TRANSFER' | 'NFT_SALE' | 'NFT_MINT' | 'UNKNOWN';
  description: string;
  fee: number;
  tokenTransfers: Array<{
    mint: string;
    symbol: string;
    amount: string;
    direction: 'in' | 'out';
  }>;
  nativeTransfer: {
    amount: number;
    direction: 'in' | 'out';
  } | null;
  // Overall direction indicator for the wallet
  direction: 'in' | 'out' | 'self' | 'unknown';
}

export interface TransactionHistory {
  transactions: TransactionEvent[];
  hasMore: boolean;
  lastSignature: string | null;
}

// ============ SOL Price Cache ============

const WSOL_MINT = 'So11111111111111111111111111111111111111112';

interface SolPriceCache {
  price: number;
  fetchedAt: number;
}

let solPriceCache: SolPriceCache | null = null;
const SOL_PRICE_CACHE_TTL_MS = 60_000; // 60 seconds

/**
 * Fetch SOL price in USD via the sol-price edge function (Pyth Hermes oracle),
 * with 60-second in-module cache. Pre-Pack-1 this called DAS getAsset on the
 * WSOL mint to read Helius's proprietary token_info.price_info.price_per_token
 * field; Triton's DAS doesn't expose that extension, so the call now goes
 * through the Pyth-backed edge function instead. Same 60s cache, same
 * null-on-failure semantics (no fallback price leak).
 */
export async function getSolPriceUsd(): Promise<number | null> {
  if (solPriceCache && (Date.now() - solPriceCache.fetchedAt) < SOL_PRICE_CACHE_TTL_MS) {
    return solPriceCache.price;
  }

  try {
    const { data, error } = await supabase.functions.invoke('sol-price');

    if (error) {
      console.warn('[PortfolioClient] sol-price edge fn error:', error.message);
      return solPriceCache?.price ?? null;
    }

    const price = data?.price;
    if (typeof price === 'number' && price > 0) {
      solPriceCache = { price, fetchedAt: Date.now() };
      debug.trace('[PortfolioClient] SOL price updated via Pyth:', price);
      return price;
    }

    console.warn('[PortfolioClient] No price in sol-price response');
    return solPriceCache?.price ?? null;
  } catch (error) {
    console.warn('[PortfolioClient] Error fetching SOL price:', error);
    return solPriceCache?.price ?? null;
  }
}

/**
 * Safely format a token balance for display using BigInt arithmetic
 * Handles very large balances without overflow
 * FIXED: Use 10n ** BigInt(decimals) to avoid precision loss
 */
function formatTokenBalance(balanceStr: string, decimals: number, maxFractionDigits = 4): string {
  if (!balanceStr || balanceStr === '0') return '0';
  
  try {
    const balance = BigInt(balanceStr);
    
    if (decimals === 0) {
      return balance.toLocaleString();
    }
    
    // FIXED: Use 10n ** BigInt(decimals) instead of BigInt(10 ** decimals)
    // to avoid precision loss for large decimals
    const divisor = 10n ** BigInt(decimals);
    const wholePart = balance / divisor;
    const fractionalPart = balance % divisor;
    
    // Format whole part with commas
    const wholeStr = wholePart.toLocaleString();
    
    if (fractionalPart === 0n || maxFractionDigits === 0) {
      return wholeStr;
    }
    
    // Format fractional part with leading zeros
    let fracStr = fractionalPart.toString().padStart(decimals, '0');
    
    // Trim trailing zeros and limit to maxFractionDigits
    fracStr = fracStr.substring(0, maxFractionDigits).replace(/0+$/, '');
    
    if (fracStr === '') {
      return wholeStr;
    }
    
    return `${wholeStr}.${fracStr}`;
  } catch (err) {
    console.warn('[PortfolioClient] Error formatting balance:', err);
    return balanceStr;
  }
}

/**
 * Calculate USD value from balance string and price
 * Returns null if calculation cannot be done safely
 * FIXED: Use 10n ** BigInt(decimals) for divisor
 */
function calculateUsdValue(balanceStr: string, decimals: number, priceUsd: number | null): number | null {
  if (!priceUsd || !balanceStr || balanceStr === '0') return null;
  
  try {
    const balance = BigInt(balanceStr);
    // FIXED: Use 10n ** BigInt(decimals) instead of BigInt(10 ** decimals)
    const divisor = 10n ** BigInt(decimals);
    
    // For very large balances, we need to be careful
    // Convert to number only after dividing
    const safeNumber = Number(balance) / Number(divisor);
    
    // Check for overflow/infinity
    if (!Number.isFinite(safeNumber) || safeNumber > Number.MAX_SAFE_INTEGER) {
      // Fallback: use BigInt division then convert
      const wholePart = Number(balance / divisor);
      if (!Number.isFinite(wholePart)) return null;
      return wholePart * priceUsd;
    }
    
    return safeNumber * priceUsd;
  } catch {
    return null;
  }
}

/**
 * Enrich holdings with on-chain bonding-curve prices for tokens DAS could not price.
 *
 * Triton DAS does not expose Helius's proprietary `price_info` extension, so SPL
 * token prices come from on-chain sources directly: bonding-curve reads here for
 * pre-graduation dextoro coins, DAMM V2 pool snapshots in `enrichWithPoolSnapshotPrices`
 * for graduated ones. Non-dextoro SPL tokens render as "n/a" in Holdings until a
 * separate price feed is wired up. We compute `priceUsd = priceSol * solPriceUsd`:
 * the same math the coin detail page uses for its "Your Position" card.
 *
 * We deliberately skip migrated pools: post-graduation the DBC `sqrtPrice` freezes at
 * the migration tx while trading moves to DAMM V2, so the DBC price would be stale.
 * For graduated tokens we trust DAS (which aggregates DAMM V2 DEX data). If DAS
 * has no price, the row stays `n/a`: identical to pre-fix behavior, no stale writes.
 */
const DBC_PRICE_LOOKUP_TIMEOUT_MS = 5000;

async function enrichWithDbcPrices(
  tokens: TokenHolding[],
  solPriceUsd: number | null,
): Promise<TokenHolding[]> {
  if (!solPriceUsd || solPriceUsd <= 0) return tokens;

  const nullPriceMints = tokens
    .filter((t) => t.priceUsd === null)
    .map((t) => t.mint);

  if (nullPriceMints.length === 0) return tokens;

  try {
    const endpoint = getSolanaEndpoint();
    if (!endpoint) return tokens;
    const client = new MeteoraDBCClient(endpoint);

    const timeoutPromise = new Promise<Map<string, PoolInfo>>((_, reject) =>
      setTimeout(
        () => reject(new Error('DBC price lookup timeout')),
        DBC_PRICE_LOOKUP_TIMEOUT_MS,
      ),
    );
    const poolInfoMap = await Promise.race([
      client.getPoolInfoBatch(nullPriceMints),
      timeoutPromise,
    ]);

    return tokens.map((t) => {
      if (t.priceUsd !== null) return t;
      const pool = poolInfoMap.get(t.mint);
      if (!pool) return t;
      // Only trust DBC price for non-migrated pools. After migration, sqrtPrice
      // is frozen at the migration tx and diverges from the live DAMM V2 price.
      if (pool.isMigrated) return t;
      const priceSol = pool.currentPrice ?? null;
      if (priceSol === null || priceSol <= 0) return t;
      const priceUsd = priceSol * solPriceUsd;
      const valueUsd = calculateUsdValue(t.balance, t.decimals, priceUsd);
      return { ...t, priceUsd, valueUsd };
    });
  } catch (err) {
    console.warn(
      '[PortfolioClient] DBC price enrichment failed, using enrichment prices only:',
      err,
    );
    return tokens;
  }
}

/**
 * Enrich holdings with live prices from the market-data pool snapshot for dextoro coins
 * DAS could not price.
 *
 * The market-data `fetchPoolSnapshot()` endpoint returns live prices for every tracked
 * dextoro pool in a single HTTP call with a 10s client-side dedup cache. It is the same
 * source the homepage cards use and the same source TokenDetail uses as its Tier-2
 * graduated-token fallback (see TokenDetail.tsx:220-221, 242-246).
 *
 * CRITICAL: the snapshot is keyed by `launched_tokens.pool_address` (the DBC pool address),
 * NOT by `damm_pool_address`. Market-data's indexer tracks trades by the stable DBC pool
 * identifier throughout the token lifecycle: pre-graduation trades come from the DBC pool
 * directly, post-graduation trades come from the DAMM V2 pool but are still written under
 * the original DBC pool address. This matches how TokenDetail looks up `poolSnapshot` and
 * is the only way the snapshot can serve as a meaningful Tier-2 fallback between live
 * DAMM V2 reserves and the stale final DBC price for graduated coins.
 *
 * Primary job: price graduated dextoro coins from DAMM V2 pool state (the only
 * authoritative on-chain price source for graduated dextoro coins post-Pack-1
 * since Helius's price_info DAS extension is no longer available via Triton).
 * It also picks up any BC token that the on-chain DBC enrichment missed.
 * Non-dextoro tokens are not in `launched_tokens` and are silently skipped.
 */
async function enrichWithPoolSnapshotPrices(
  tokens: TokenHolding[],
  solPriceUsd: number | null,
): Promise<TokenHolding[]> {
  if (!solPriceUsd || solPriceUsd <= 0) return tokens;

  const nullPriceMints = tokens
    .filter((t) => t.priceUsd === null)
    .map((t) => t.mint);

  if (nullPriceMints.length === 0) return tokens;

  try {
    const { data: rows, error } = await supabase
      .from('launched_tokens')
      .select('mint_address, pool_address')
      .in('mint_address', nullPriceMints)
      .not('pool_address', 'is', null);

    if (error) {
      console.warn('[PortfolioClient] launched_tokens lookup failed:', error);
      return tokens;
    }
    if (!rows || rows.length === 0) return tokens;

    const mintToPoolAddress = new Map<string, string>();
    for (const row of rows) {
      if (row.pool_address) {
        mintToPoolAddress.set(row.mint_address, row.pool_address);
      }
    }
    if (mintToPoolAddress.size === 0) return tokens;

    const snapshot = await fetchPoolSnapshot();

    return tokens.map((t) => {
      if (t.priceUsd !== null) return t;
      const poolAddress = mintToPoolAddress.get(t.mint);
      if (!poolAddress) return t;
      const entry = snapshot[poolAddress];
      const priceSol = entry?.price ?? null;
      if (priceSol === null || priceSol <= 0) return t;
      const priceUsd = priceSol * solPriceUsd;
      const valueUsd = calculateUsdValue(t.balance, t.decimals, priceUsd);
      return { ...t, priceUsd, valueUsd };
    });
  } catch (err) {
    console.warn(
      '[PortfolioClient] Pool snapshot price enrichment failed, using RPC/DBC prices only:',
      err,
    );
    return tokens;
  }
}

/**
 * Fetch zero-balance token accounts using standard RPC getTokenAccountsByOwner.
 * DAS API only returns assets with balances, so we need this separate call
 * to discover empty accounts that can be closed to reclaim rent.
 */
async function fetchEmptyTokenAccounts(
  rpcUrl: string,
  walletAddress: string,
  nonZeroMints: Set<string>
): Promise<TokenHolding[]> {
  try {
    // Fetch from both standard Token Program and Token-2022 in parallel
    const [standardResponse, token2022Response] = await Promise.all([
      fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 'token-accounts',
          method: 'getTokenAccountsByOwner',
          params: [
            walletAddress,
            { programId: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' },
            { encoding: 'jsonParsed' },
          ],
        }),
      }),
      fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 'token-accounts-2022',
          method: 'getTokenAccountsByOwner',
          params: [
            walletAddress,
            { programId: 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb' },
            { encoding: 'jsonParsed' },
          ],
        }),
      }),
    ]);

    const [standardResult, token2022Result] = await Promise.all([
      standardResponse.json(),
      token2022Response.json(),
    ]);

    const allAccounts = [
      ...(standardResult.result?.value || []),
      ...(token2022Result.result?.value || []),
    ];

    const emptyAccounts: TokenHolding[] = [];

    for (const account of allAccounts) {
      const parsed = account.account?.data?.parsed?.info;
      if (!parsed) continue;

      const mint = parsed.mint;
      const amount = parsed.tokenAmount?.amount || '0';
      const decimals = parsed.tokenAmount?.decimals || 0;

      // Only include zero-balance accounts not already in the holdings list
      if (amount !== '0' || nonZeroMints.has(mint)) continue;

      // Skip frozen accounts — can't be closed without token authority action
      if (parsed.state === 'frozen') continue;

      // Skip accounts where closeAuthority is set to someone other than the wallet
      if (parsed.closeAuthority && parsed.closeAuthority !== walletAddress) continue;

      emptyAccounts.push({
        mint,
        symbol: 'UNKNOWN',
        name: 'Unknown Token',
        imageUrl: null,
        balance: '0',
        decimals,
        uiAmount: '0',
        priceUsd: null,
        valueUsd: null,
        tokenAccountAddress: account.pubkey,
      });
    }

    return emptyAccounts;
  } catch (error) {
    console.warn('[PortfolioClient] Error fetching empty token accounts:', error);
    return [];
  }
}

/**
 * Enrich empty token accounts with metadata from getAssetBatch
 */
async function enrichEmptyAccounts(
  rpcUrl: string,
  accounts: TokenHolding[]
): Promise<TokenHolding[]> {
  if (accounts.length === 0) return accounts;

  try {
    const mints = accounts.map(a => a.mint);
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'asset-batch',
        method: 'getAssetBatch',
        params: {
          ids: mints,
          displayOptions: { showFungible: true },
        },
      }),
    });

    const result = await response.json();
    if (result.error || !Array.isArray(result.result)) return accounts;

    const metadataMap = new Map<string, { symbol: string; name: string; imageUrl: string | null }>();
    for (const asset of result.result) {
      if (!asset || !asset.id) continue;
      const tokenInfo = asset.token_info || {};
      const content = asset.content || {};
      const metadata = content.metadata || {};
      metadataMap.set(asset.id, {
        symbol: tokenInfo.symbol || metadata.symbol || 'UNKNOWN',
        name: metadata.name || tokenInfo.symbol || 'Unknown Token',
        imageUrl: content.links?.image || content.files?.[0]?.uri || null,
      });
    }

    return accounts.map(account => {
      const meta = metadataMap.get(account.mint);
      if (meta) {
        return { ...account, ...meta };
      }
      return account;
    });
  } catch (error) {
    console.warn('[PortfolioClient] Error enriching empty accounts:', error);
    return accounts;
  }
}

/**
 * Fetch wallet holdings using getAssetsByOwner
 * Now includes SOL price for total value calculation
 */
export async function getWalletHoldings(walletAddress: string): Promise<PortfolioHoldings> {
  const rpcUrl = getSolanaEndpoint();
  if (!rpcUrl) {
    console.warn('[PortfolioClient] No RPC URL configured');
    return { solBalance: 0, solValueUsd: null, tokens: [], emptyAccounts: [], totalValueUsd: null };
  }

  try {
    // Fetch SOL price in parallel with balance requests
    const [solPrice, balanceResponse, assetsResponse] = await Promise.all([
      getSolPriceUsd(),
      fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 'sol-balance',
          method: 'getBalance',
          params: [walletAddress],
        }),
      }),
      fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 'assets-by-owner',
          method: 'getAssetsByOwner',
          params: {
            ownerAddress: walletAddress,
            page: 1,
            limit: 100,
            displayOptions: {
              showFungible: true,
              showNativeBalance: true,
            },
          },
        }),
      }),
    ]);

    const balanceResult = await balanceResponse.json();
    const solBalance = balanceResult.result?.value ? balanceResult.result.value / 1e9 : 0;

    // Calculate SOL USD value
    const solValueUsd = solPrice ? solBalance * solPrice : null;

    const assetsResult = await assetsResponse.json();
    
    if (assetsResult.error) {
      console.error('[PortfolioClient] DAS error:', assetsResult.error);
      return { solBalance, solValueUsd, tokens: [], emptyAccounts: [], totalValueUsd: solValueUsd };
    }

    const items = assetsResult.result?.items || [];
    
    // Map all fungible tokens
    const allTokens: TokenHolding[] = items
      .filter((item: any) =>
        item.interface === 'FungibleToken' ||
        item.interface === 'FungibleAsset'
      )
      .map((item: any) => {
        const tokenInfo = item.token_info || {};
        const content = item.content || {};
        const metadata = content.metadata || {};

        // Keep balance as string to avoid BigInt overflow
        const balanceStr = String(tokenInfo.balance || '0');
        const decimals = tokenInfo.decimals || 0;

        // Pre-Pack-1 this read tokenInfo.price_info.price_per_token (Helius DEX
        // aggregate price, a Helius-proprietary DAS extension). Triton DAS doesn't
        // expose price_info, so SPL tokens start with priceUsd: null. dextoro coins
        // get prices below via enrichWithDbcPrices + enrichWithPoolSnapshotPrices;
        // non-dextoro SPL tokens (USDC, JTO, BONK, etc.) render as "n/a" until a
        // separate price feed is wired up.
        const priceUsd: number | null = null;
        const valueUsd = calculateUsdValue(balanceStr, decimals, priceUsd);

        return {
          mint: item.id,
          symbol: tokenInfo.symbol || metadata.symbol || 'UNKNOWN',
          name: metadata.name || tokenInfo.symbol || 'Unknown Token',
          imageUrl: content.links?.image || content.files?.[0]?.uri || null,
          balance: balanceStr,
          decimals,
          uiAmount: formatTokenBalance(balanceStr, decimals, 4),
          priceUsd,
          valueUsd,
        };
      });

    // Filter to non-zero balances for main holdings
    const nonZeroTokens = allTokens.filter((token) => token.balance !== '0' && BigInt(token.balance) > 0n);

    // Fill in USD prices for coins DAS couldn't price, in two steps:
    //   1. Bonding curve (pre-graduation dextoro coins) via on-chain DBC pool state
    //   2. Graduated dextoro coins via the market-data pool snapshot (live prices keyed
    //      by the stable DBC pool address, covers both pre- and post-graduation trades)
    // Each step only runs on the still-null-price subset, so zero-cost when not needed.
    const afterDbc = await enrichWithDbcPrices(nonZeroTokens, solPrice);
    const tokens = await enrichWithPoolSnapshotPrices(afterDbc, solPrice);

    // Fetch zero-balance token accounts via standard RPC (DAS API doesn't return them)
    const nonZeroMints = new Set(tokens.map(t => t.mint));
    let emptyAccounts = await fetchEmptyTokenAccounts(rpcUrl, walletAddress, nonZeroMints);

    // Enrich empty accounts with token metadata (name, symbol, image)
    if (emptyAccounts.length > 0) {
      emptyAccounts = await enrichEmptyAccounts(rpcUrl, emptyAccounts);
    }

    // Calculate totals including SOL
    const tokensValueUsd = tokens.reduce((sum, t) => sum + (t.valueUsd || 0), 0);
    const totalValueUsd = (solValueUsd || 0) + tokensValueUsd;

    debug.trace('[PortfolioClient] Holdings fetched:', {
      solBalance,
      solValueUsd,
      tokenCount: tokens.length,
      emptyAccountCount: emptyAccounts.length,
      totalValueUsd: totalValueUsd > 0 ? totalValueUsd : null,
    });

    return {
      solBalance,
      solValueUsd,
      tokens,
      emptyAccounts,
      totalValueUsd: totalValueUsd > 0 ? totalValueUsd : null,
    };
  } catch (error) {
    console.error('[PortfolioClient] Error fetching holdings:', error);
    return { solBalance: 0, solValueUsd: null, tokens: [], emptyAccounts: [], totalValueUsd: null };
  }
}

/**
 * Map enriched transaction type taxonomy to our TransactionEvent type
 */
function mapEnhancedTxType(txType: string): TransactionEvent['type'] {
  const typeMap: Record<string, TransactionEvent['type']> = {
    'SWAP': 'SWAP',
    'TRANSFER': 'TRANSFER',
    'NFT_SALE': 'NFT_SALE',
    'NFT_BID': 'UNKNOWN',
    'NFT_LISTING': 'UNKNOWN',
    'NFT_MINT': 'NFT_MINT',
    'BURN': 'UNKNOWN',
    'UNKNOWN': 'UNKNOWN',
    // Additional enriched types
    'COMPRESSED_NFT_MINT': 'NFT_MINT',
    'NFT_CANCEL_LISTING': 'UNKNOWN',
    'STAKE_SOL': 'UNKNOWN',
    'UNSTAKE_SOL': 'UNKNOWN',
    'TOKEN_MINT': 'UNKNOWN',
    'CREATE_POOL': 'UNKNOWN',
    'ADD_LIQUIDITY': 'UNKNOWN',
    'REMOVE_LIQUIDITY': 'UNKNOWN',
    'BORROW': 'UNKNOWN',
    'REPAY': 'UNKNOWN',
    'DEPOSIT': 'UNKNOWN',
    'WITHDRAW': 'UNKNOWN',
  };
  return typeMap[txType?.toUpperCase()] || 'UNKNOWN';
}

/**
 * Determine overall direction for a transaction relative to the wallet
 */
function determineDirection(
  walletAddress: string,
  tokenTransfers: TransactionEvent['tokenTransfers'],
  nativeTransfer: TransactionEvent['nativeTransfer']
): TransactionEvent['direction'] {
  const walletLower = walletAddress.toLowerCase();
  
  // Check token transfers
  const hasIncoming = tokenTransfers.some(t => t.direction === 'in');
  const hasOutgoing = tokenTransfers.some(t => t.direction === 'out');
  
  // Check native transfer
  const nativeIn = nativeTransfer?.direction === 'in';
  const nativeOut = nativeTransfer?.direction === 'out';
  
  // Determine overall direction
  if ((hasIncoming || nativeIn) && (hasOutgoing || nativeOut)) {
    // Both directions - likely a swap
    return 'self';
  }
  if (hasIncoming || nativeIn) {
    return 'in';
  }
  if (hasOutgoing || nativeOut) {
    return 'out';
  }
  
  return 'unknown';
}

/**
 * Parse enriched transaction to our TransactionEvent format
 */
function parseEnhancedTransaction(tx: any, walletAddress: string): TransactionEvent {
  const signature = tx.signature;
  const timestamp = tx.timestamp ? tx.timestamp * 1000 : Date.now();
  const type = mapEnhancedTxType(tx.type || 'UNKNOWN');
  const description = tx.description || tx.type || 'Transaction';
  const fee = tx.fee || 0;
  const walletLower = walletAddress.toLowerCase();
  
  // Parse token transfers
  const tokenTransfers: TransactionEvent['tokenTransfers'] = [];
  if (tx.tokenTransfers && Array.isArray(tx.tokenTransfers)) {
    for (const transfer of tx.tokenTransfers) {
      const toAccount = transfer.toUserAccount || transfer.toTokenAccount || '';
      const fromAccount = transfer.fromUserAccount || transfer.fromTokenAccount || '';
      
      const isIncoming = toAccount.toLowerCase() === walletLower;
      const isOutgoing = fromAccount.toLowerCase() === walletLower;
      
      if (isIncoming || isOutgoing) {
        tokenTransfers.push({
          mint: transfer.mint || '',
          symbol: transfer.symbol || transfer.tokenStandard || 'TOKEN',
          amount: String(transfer.tokenAmount || 0),
          direction: isIncoming ? 'in' : 'out',
        });
      }
    }
  }
  
  // Parse native SOL transfers
  let nativeTransfer: TransactionEvent['nativeTransfer'] = null;
  if (tx.nativeTransfers && Array.isArray(tx.nativeTransfers)) {
    for (const transfer of tx.nativeTransfers) {
      const toAccount = transfer.toUserAccount || '';
      const fromAccount = transfer.fromUserAccount || '';
      
      const isIncoming = toAccount.toLowerCase() === walletLower;
      const isOutgoing = fromAccount.toLowerCase() === walletLower;
      const amount = (transfer.amount || 0) / 1e9; // lamports to SOL
      
      if (amount > 0 && (isIncoming || isOutgoing)) {
        nativeTransfer = { amount, direction: isIncoming ? 'in' : 'out' };
        break; // Take first significant transfer
      }
    }
  }
  
  const direction = determineDirection(walletAddress, tokenTransfers, nativeTransfer);
  
  return {
    signature,
    timestamp,
    type,
    description,
    fee,
    tokenTransfers,
    nativeTransfer,
    direction,
  };
}

/**
 * Fetch enriched transaction history using Enhanced Transactions REST API (via helius-transactions edge fn, Pack 3 will refactor)
 * Falls back to basic signatures if enriched API fails
 */
export async function getTransactionHistory(
  walletAddress: string,
  options: { limit?: number; before?: string } = {}
): Promise<TransactionHistory> {
  const rpcUrl = getSolanaEndpoint();
  if (!rpcUrl) {
    console.warn('[PortfolioClient] No RPC URL configured');
    return { transactions: [], hasMore: false, lastSignature: null };
  }

  const limit = options.limit || 20;

  try {
    // Step 1: Get signatures for address via RPC
    const signaturesResponse = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'get-signatures',
        method: 'getSignaturesForAddress',
        params: [
          walletAddress,
          {
            limit: limit + 1,
            ...(options.before && { before: options.before }),
          },
        ],
      }),
    });

    const signaturesResult = await signaturesResponse.json();
    
    if (signaturesResult.error) {
      console.error('[PortfolioClient] Signatures error:', signaturesResult.error);
      return { transactions: [], hasMore: false, lastSignature: null };
    }

    const signatures = signaturesResult.result || [];
    const hasMore = signatures.length > limit;
    const signaturesToUse = signatures.slice(0, limit);

    if (signaturesToUse.length === 0) {
      return { transactions: [], hasMore: false, lastSignature: null };
    }

    const signatureList = signaturesToUse.map((s: any) => s.signature);

    // Step 2: Try to fetch enriched transactions via Enhanced Transactions REST API (via helius-transactions edge fn, Pack 3 will refactor)
    try {
      const { data: enrichedData, error: enrichedError } = await supabase.functions.invoke(
        'helius-transactions',
        {
          body: { signatures: signatureList },
        }
      );

      if (!enrichedError && enrichedData?.transactions && Array.isArray(enrichedData.transactions)) {
        const transactions: TransactionEvent[] = enrichedData.transactions.map((tx: any) => 
          parseEnhancedTransaction(tx, walletAddress)
        );

        debug.trace('[PortfolioClient] Enriched transactions fetched:', transactions.length);

        return {
          transactions,
          hasMore,
          lastSignature: signaturesToUse[signaturesToUse.length - 1]?.signature || null,
        };
      }
      
      console.warn('[PortfolioClient] Enriched API returned no data, falling back');
    } catch (enrichedError) {
      console.warn('[PortfolioClient] Enriched API failed, using basic signatures:', enrichedError);
    }

    // Fallback: Map basic signatures to transaction events
    const transactions: TransactionEvent[] = signaturesToUse.map((sig: any) => {
      const isFailed = sig.err !== null;
      
      return {
        signature: sig.signature,
        timestamp: sig.blockTime ? sig.blockTime * 1000 : Date.now(),
        type: 'UNKNOWN' as const,
        description: isFailed ? 'Failed transaction' : 'Transaction',
        fee: 0,
        tokenTransfers: [],
        nativeTransfer: null,
        direction: 'unknown' as const,
      };
    });

    debug.trace('[PortfolioClient] Basic transactions fetched:', transactions.length);

    return {
      transactions,
      hasMore,
      lastSignature: signaturesToUse[signaturesToUse.length - 1]?.signature || null,
    };
  } catch (error) {
    console.error('[PortfolioClient] Error fetching transactions:', error);
    return { transactions: [], hasMore: false, lastSignature: null };
  }
}
